import { initializeApp } from 'firebase/app';
import {
  getAuth,
  signInAnonymously,
  signInWithCustomToken,
  onAuthStateChanged,
} from 'firebase/auth';
import { getStorage } from 'firebase/storage';
import { getFirestore } from 'firebase/firestore';

const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);
const storage = getStorage(app);
const firestore = getFirestore(app);

// Helper function to sign in anonymously
export const signInAnonymousUser = async () => {
  try {
    const userCredential = await signInAnonymously(auth);
    return userCredential.user;
  } catch (error) {
    console.error('[Firebase] Error signing in anonymously: ', error);
    throw error;
  }
};

// Function to sign in with Auth0 token
export const signInWithAuth0Token = async (auth0Token) => {
  let currentUser = null;

  try {
    // Only sign out if there's an existing user
    currentUser = auth.currentUser;
    console.log('[Firebase] Current auth state:', {
      hasCurrentUser: !!currentUser,
      currentUser: currentUser
        ? {
            uid: currentUser.uid,
            email: currentUser.email,
          }
        : null,
    });

    if (currentUser) {
      console.log('[Firebase] Signing out existing user before new sign in');
      await auth.signOut();
    }

    // Call your backend endpoint to exchange Auth0 token for Firebase token
    console.log('[Firebase] Exchanging Auth0 token for Firebase token');
    const response = await fetch(
      'https://europe-west10-arbitration-app-456816.cloudfunctions.net/exchangeAuth0Token',
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth0Token}`,
        },
      },
    );

    if (!response.ok) {
      const errorData = await response.json();
      console.error('[Firebase] Token exchange failed:', {
        status: response.status,
        error: errorData,
      });
      throw new Error(errorData.details || 'Failed to get Firebase token');
    }

    const { firebaseToken } = await response.json();
    console.log('[Firebase] Successfully exchanged token');

    // Sign in to Firebase with the custom token
    console.log('[Firebase] Signing in with custom token');
    const userCredential = await signInWithCustomToken(auth, firebaseToken);

    // Wait for the auth state to be updated
    await new Promise((resolve, reject) => {
      const unsubscribe = onAuthStateChanged(auth, (user) => {
        unsubscribe();
        if (user) {
          resolve(user);
        } else {
          reject(new Error('Firebase auth state not updated after sign in'));
        }
      });
    });

    console.log('[Firebase] Successfully signed in with custom token:', {
      uid: userCredential.user.uid,
      email: userCredential.user.email,
    });

    return userCredential.user;
  } catch (error) {
    console.error('[Firebase] Error signing in with Auth0 token:', {
      error,
      hasCurrentUser: !!auth.currentUser,
    });

    // Only clear auth state if we were in the middle of signing in
    if (auth.currentUser) {
      console.log('[Firebase] Clearing auth state due to error');
      await auth.signOut();
    }

    throw error;
  }
};

export { auth, storage, firestore };
